/** Where a game goes when it ends: initials, the tables, the Daily result, back to a menu. */

import type { TableId } from '../../game/hiscores';
import type { Session } from '../../game/session';
import { dailyNumber } from '../../caves/generator';
import { practiceKey } from '../../platform/records';
import type { CaveOutcome } from '../../game/session';
import type { PendingScore, PlayRequest, SceneHost } from '../../ui/scene';
import type { CaveTurn } from './caveSource';

function tableFor(request: PlayRequest): TableId | null {
  if (request.mode === 'classic' || request.mode === 'deep') return request.mode;
  if (request.mode === 'expedition') return 'expedition';
  return null;
}

/**
 * A multi-cave game is over (or quit): record it and ask for initials if a score made the table.
 * `log` is the game's log (game/gameLog); with it the scores can also go to the world tables.
 */
export function finishGame(host: SceneHost, request: PlayRequest, session: Session, labelFor: (player: number) => string, log?: string): void {
  const { scores, records, options } = host.services;
  const table = tableFor(request);
  if (!table) return void host.go({ to: 'title', skipPress: true });
  if (request.mode === 'expedition') {
    const p = session.players[0];
    if (p) records.recordExpedition(p.position + 1, p.score);
  }
  const relaxed = options.get().speed === 'relaxed';
  const pending: PendingScore[] = [];
  session.players.forEach((p, i) => {
    if (!scores.qualifies(table, p.score)) return;
    pending.push({
      table,
      score: p.score,
      reached: labelFor(i),
      relaxed,
      who: session.players.length > 1 ? `PLAYER ${i + 1}` : '',
      then: [],
      ...(log === undefined ? {} : { proof: { log, player: i } }),
    });
  });
  // Best score first, so the second entry's rank is judged against a table that already has the first.
  pending.sort((a, b) => b.score - a.score);
  const [first, ...rest] = pending;
  if (first) host.go({ to: 'initials', entry: { ...first, then: rest } });
  else host.go({ to: 'hiscores', table });
}

/** A one-cave mode is over: Daily Dig, a Practice win, the demo. */
export function finishOneShot(
  host: SceneHost,
  request: PlayRequest,
  turn: CaveTurn,
  cave: { outcome: CaveOutcome; score: number; seconds: number; diamonds: number },
  log?: string,
): void {
  const { records } = host.services;
  if (request.mode === 'daily') {
    const run = { won: cave.outcome === 'won', score: cave.score, seconds: cave.seconds, diamonds: cave.diamonds, ...(log === undefined ? {} : { proof: log }) };
    records.recordDaily(dailyNumber(request.year, request.month, request.day), run);
    return void host.go({ to: 'daily', last: run });
  }
  if (request.mode === 'practice') {
    if (cave.outcome === 'won') records.recordPractice(practiceKey(turn.def, turn.level), cave.score);
    return void (request.back === 'pop' ? host.back() : host.go(request.back));
  }
  host.go({ to: 'title', skipPress: true });
}
